/**
 * KAN-883: вид CRM "Лиды по дням" (?view=stats): период, строки таблицы и
 * переход из цифры в таблицу CRM.
 * Период: ?from=&to=; без них - 30 дней по сегодня включительно (в адрес не
 * пишется). Цифра открывает таблицу ?view=table&event=<событие>&from=&to=
 * (отказ - ещё outcome=lost); менеджер сохраняется, сортировка, кнопки быстрого
 * выбора, мусор, поиск, атрибут, город и организация снимаются (строк = цифре).
 * KAN-910: быстрый период именем ?period=today|yesterday|7d|30d (общий параметр с таблицей,
 * lib/urlPeriod), даты ?from=&to= только из календаря; без периода пишется ?period=30d.
 * Переход из итога за быстрый период несёт в таблицу имя, а не даты.
 */
import type { CrmDailyMetric, CrmDailyMetrics, CrmLeadsEvent } from '@/types/crm';
import type { CrmTableOutcome } from '@/types/crmPipeline';
import type { PeriodChoice, PeriodPreset } from './periodPresets';
import type { CrmRange } from './crmTable';
import { CRM_PERIOD_PARAM, CRM_TABLE_PERIOD_KEYS } from './crmTablePeriod';
import { parseUrlPeriod, PERIOD_URL_NAME, withUrlPeriod, type UrlPeriodValue } from './urlPeriod';
import { CRM_SORT_PARAM } from './crmTableSort';
import { CRM_SHORTCUT_PARAM } from './crmTableShortcuts';
import { CRM_ATTRIBUTE_PARAM, CRM_LOCATION_PARAM } from './crmTableUrlParams';
import { CRM_FILTER_PARAM } from './crmLeadFilters';

export const CRM_STATS_DEFAULT_PRESET: PeriodPreset = 'month';
/** Период "Лиды по дням" по умолчанию (имя в адресе). */
export const CRM_STATS_DEFAULT_PERIOD = PERIOD_URL_NAME[CRM_STATS_DEFAULT_PRESET];

/** Быстрые кнопки периода "Лиды по дням". */
export const CRM_STATS_PERIOD_CHOICES: readonly PeriodChoice[] = ['today', 'yesterday', 'week', 'month'];

export interface CrmStatsPeriod {
  range: CrmRange;
  /** Быстрый пункт, если период задан именем; даты календаря - null. */
  preset: PeriodPreset | null;
}

/**
 * Период из адреса: имя главнее дат; без имени и дат - 30 дней по сегодня.
 * ?period=all ("Всё время" таблицы) здесь не понимается: 30 дней.
 */
export function parseCrmStatsPeriod(params: URLSearchParams, now: Date = new Date()): CrmStatsPeriod {
  const parsed = parseUrlPeriod(params, CRM_TABLE_PERIOD_KEYS, CRM_STATS_DEFAULT_PRESET, { allowAll: false, now });
  const preset = parsed.choice && parsed.choice !== 'all' ? parsed.choice : null;
  // range не null: allowAll false и умолчание - период.
  return { range: parsed.range as CrmRange, preset };
}

/** Адрес с периодом: быстрый пункт - ?period=<имя>, даты календаря - ?from=&to=. */
export function withCrmStatsPeriod(prev: URLSearchParams, value: PeriodPreset | CrmRange): URLSearchParams {
  return withUrlPeriod(prev, CRM_TABLE_PERIOD_KEYS, value);
}

/** Столбцы с цифрами в порядке таблицы; '%' стоит после "Лиды". */
export const CRM_STATS_COLUMNS: readonly { metric: CrmDailyMetric; label: string }[] = [
  { metric: 'sent', label: 'Отправлено' },
  { metric: 'leads', label: 'Лиды' },
  { metric: 'leads_replied', label: 'Ответили' },
  { metric: 'leads_inbound', label: 'Написали сами' },
  { metric: 'leads_refused', label: 'Отказ' },
  { metric: 'handed', label: 'Передано' },
  { metric: 'taken', label: 'Взято' },
  { metric: 'won', label: 'Продажи' },
];

export const CRM_STATS_METRIC_LABEL: Record<CrmDailyMetric, string> = Object.fromEntries(
  CRM_STATS_COLUMNS.map((c) => [c.metric, c.label])
) as Record<CrmDailyMetric, string>;

/** Доля лидов от отправленных: "30%", при нуле отправленных "-". */
export function crmLeadShare(row: Pick<CrmDailyMetrics, 'leads' | 'sent'>): string {
  return row.sent > 0 ? `${Math.round((row.leads / row.sent) * 100)}%` : '-';
}

/** Событие таблицы для цифры и исход (только у отказа). */
const METRIC_DRILL: Record<CrmDailyMetric, { event: CrmLeadsEvent; outcome?: CrmTableOutcome }> = {
  sent: { event: 'sent' },
  leads: { event: 'lead' },
  leads_replied: { event: 'lead_replied' },
  leads_inbound: { event: 'lead_inbound' },
  leads_refused: { event: 'lead', outcome: 'lost' },
  handed: { event: 'handed' },
  taken: { event: 'taken' },
  won: { event: 'won' },
};

/**
 * Что снимает переход в таблицу: сортировка, период, исход, признаки кнопок и фильтры, которых нет у
 * "Лиды по дням" (иначе строк меньше цифры). Менеджер остаётся: цифры считаются с ним.
 */
const DRILL_RESET_KEYS = [
  CRM_SORT_PARAM,
  CRM_PERIOD_PARAM.period,
  CRM_PERIOD_PARAM.outcome,
  CRM_SHORTCUT_PARAM.lead,
  CRM_SHORTCUT_PARAM.idle,
  CRM_SHORTCUT_PARAM.junk,
  CRM_FILTER_PARAM.search,
  CRM_ATTRIBUTE_PARAM.attr,
  CRM_ATTRIBUTE_PARAM.value,
  CRM_LOCATION_PARAM.city,
  CRM_LOCATION_PARAM.company,
  CRM_LOCATION_PARAM.companyId,
] as const;

/** Адрес таблицы CRM с лидами цифры за период: даты или быстрый пункт (именем). */
export function crmStatsDrillParams(
  prev: URLSearchParams,
  metric: CrmDailyMetric,
  period: Exclude<UrlPeriodValue, 'all'>
): URLSearchParams {
  const { event, outcome } = METRIC_DRILL[metric];
  const reset = new URLSearchParams(prev);
  DRILL_RESET_KEYS.forEach((key) => reset.delete(key));
  const next = withUrlPeriod(reset, CRM_TABLE_PERIOD_KEYS, period);
  next.set('view', 'table');
  next.set(CRM_SHORTCUT_PARAM.event, event);
  if (outcome) next.set(CRM_PERIOD_PARAM.outcome, outcome);
  return next;
}

/** KAN-928: лиды дня без отказов (leads - leads_refused), не меньше 0. Для графика. */
export function netLeads(day: Pick<CrmDailyMetrics, 'leads' | 'leads_refused'>): number {
  return Math.max(0, day.leads - day.leads_refused);
}

/** KAN-928: дни для графика: leads заменены на лиды без отказов; исходные дни не меняются. */
export function crmStatsChartDays<T extends CrmDailyMetrics>(days: readonly T[]): T[] {
  return days.map((day) => ({ ...day, leads: netLeads(day) }));
}
